/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import clsx from 'clsx'
import UiSelectWithGraphics from 'components/Ui/UiSelectWithGraphics'
import UiInput from 'components/Ui/UiInput'
import {LeadSource} from 'types/enums/LeadSource'
import {getLeadSourceDescriptors} from 'utils/leads/leadsUtils'

interface LeadSourceSelectProps {
    value: string | null | undefined
    onChange: (value: string | null) => void
    label: string
    className?: string
    error?: string
}

const PRESET_SOURCES: LeadSource[] = Object.values(LeadSource).filter((value) => value !== LeadSource.Other)

const LeadSourceSelect: React.FC<LeadSourceSelectProps> = ({
    value,
    onChange,
    label,
    className,
    error,
}) => {
    const { t } = useTranslation()
    const normalizedValue = (value ?? '').trim()
    const [customModeActive, setCustomModeActive] = useState(false)

    const options = useMemo(() => {
        const presets = getLeadSourceDescriptors(t).filter(
            (descriptor) => descriptor.value !== LeadSource.Other
        )
        return [...presets, { value: 'custom', label: t('Other') }]
    }, [t])

    const selectedOption = useMemo(() => {
        if (normalizedValue && PRESET_SOURCES.includes(normalizedValue as LeadSource)) {
            return normalizedValue
        }
        if (normalizedValue || customModeActive) {
            return 'custom'
        }
        return null
    }, [normalizedValue, customModeActive])

    const customValue =
        selectedOption === 'custom' &&
        normalizedValue &&
        normalizedValue !== LeadSource.Other
            ? normalizedValue
            : ''

    const handleSelectChange = (val: string | number | null) => {
        const stringVal = val ? String(val) : null
        if (stringVal === 'custom') {
            setCustomModeActive(true)
            onChange(null)
            return
        }

        setCustomModeActive(false)
        onChange(stringVal)
    }

    const handleCustomValueChange = (next: string) => {
        setCustomModeActive(true)
        onChange(next.trim() || null)
    }

    return (
        <div className={clsx('LeadSourceSelect flex flex-col', className)}>
            <UiSelectWithGraphics
                label={label}
                options={options}
                selected={selectedOption}
                setSelected={handleSelectChange}
            />
            {selectedOption === 'custom' && (
                <UiInput
                    placeholder={t('Enter custom origin')}
                    value={customValue}
                    onChange={(e) => handleCustomValueChange(e.target.value)}
                    hasError={!!error}
                    autoComplete="one-time-code"
                />
            )}
            {selectedOption === 'custom' && error && (
                <span className="text-xs text-red-500 mt-1">{error}</span>
            )}
        </div>
    )
}

export default LeadSourceSelect
